import { useCallback, useEffect, useRef, useState } from "react";
import { ulid } from "ulid";
import { TeamForm } from "./components/TeamForm";
import { FolderPicker } from "./components/FolderPicker";
import { UploadProgress } from "./components/UploadProgress";
import { loadConfig, type AppConfig } from "./lib/config";
import { createTransport, type UploadTransport } from "./lib/transport";
import { Uploader } from "./lib/uploader";
import { buildTasks } from "./lib/tasks";
import { writeDoneManifest, writeStartManifest } from "./lib/manifest";
import { forgetSession, loadCompleted, readSession, saveSession } from "./lib/resume";
import type { FileTask, TeamForm as TeamFormValues, UploadStats } from "./lib/types";

type Step = "form" | "pick" | "upload";

const EMPTY_STATS: UploadStats = {
  total: 0,
  done: 0,
  skipped: 0,
  failed: 0,
  inFlight: 0,
  bytesTotal: 0,
  bytesDone: 0,
  bytesPerSecond: null,
  etaSeconds: null,
};

export default function App() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("form");
  const [form, setForm] = useState<TeamFormValues | null>(null);
  const [stats, setStats] = useState<UploadStats>(EMPTY_STATS);
  const [failed, setFailed] = useState<FileTask[]>([]);
  const [finished, setFinished] = useState(false);
  const [paused, setPaused] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);
  /** Set when a previous session left unfinished work, so the picker can offer resume. */
  const [resumable, setResumable] = useState<{ sessionId: string; form: TeamFormValues } | null>(
    null
  );

  const transportRef = useRef<UploadTransport | null>(null);
  const uploaderRef = useRef<Uploader | null>(null);
  const tasksRef = useRef<FileTask[]>([]);
  const sessionIdRef = useRef<string>("");

  useEffect(() => {
    loadConfig()
      .then((c) => {
        setConfig(c);
        transportRef.current = createTransport(c);
      })
      .catch((e: Error) => setConfigError(e.message));

    const prev = readSession();
    if (prev) setResumable({ sessionId: prev.sessionId, form: prev.form as TeamFormValues });
  }, []);

  // A half-finished 12 GB upload is expensive to lose; make the tab hard to close by accident.
  useEffect(() => {
    if (step !== "upload" || finished) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [step, finished]);

  const runUpload = useCallback(
    async (tasks: FileTask[], values: TeamFormValues, sessionId: string, writeStart: boolean) => {
      const transport = transportRef.current;
      const cfg = config;
      if (!transport || !cfg) return;

      tasksRef.current = tasks;
      sessionIdRef.current = sessionId;
      setFinished(false);
      setPaused(false);
      setFailed([]);
      setFatal(null);

      if (writeStart) {
        try {
          await writeStartManifest(transport, cfg.prefix, values, sessionId, tasks);
        } catch (e) {
          // The manifest is the only record of who uploaded what, so a failure
          // here almost always means the SAS or CORS is wrong - stop early
          // rather than let the team upload 12 GB into a black hole.
          setFatal(
            `Oturum kaydı yazılamadı, yükleme başlatılmadı. ${
              e instanceof Error ? e.message : String(e)
            }`
          );
          return;
        }
      }

      const uploader = new Uploader(tasks, transport, sessionId, {
        onStats: setStats,
        onTaskChange: () => {
          /* aggregate stats drive the UI; per-task rerenders would thrash at 1.000+ files */
        },
        onDone: async (finishedTasks) => {
          setFailed(finishedTasks.filter((t) => t.status === "failed"));
          if (uploader.aborted) return;
          setFinished(true);
          try {
            await writeDoneManifest(transport, cfg.prefix, values, sessionId, finishedTasks);
          } catch {
            // Images are already delivered; a missing done-manifest is not worth failing over.
          }
          if (finishedTasks.every((t) => t.status === "done" || t.status === "skipped")) {
            forgetSession();
          }
        },
      });

      uploaderRef.current = uploader;
      await uploader.start();
    },
    [config]
  );

  async function handleStart(files: File[]) {
    if (!form || !config) return;

    // Reuse the stored session id when the picked folder matches an unfinished
    // run, so already-uploaded files are skipped instead of re-sent.
    const reuse = resumable && resumable.form.provinceSlug === form.provinceSlug;
    const sessionId = reuse ? resumable.sessionId : ulid();

    const tasks = buildTasks(files, form, config.prefix, sessionId);

    if (reuse) {
      const completed = await loadCompleted(sessionId);
      for (const t of tasks) if (completed.has(t.key)) t.status = "skipped";
    }

    saveSession(sessionId, form);
    setStep("upload");
    setStats({ ...EMPTY_STATS, total: tasks.length });
    void runUpload(tasks, form, sessionId, true);
  }

  function handlePause() {
    uploaderRef.current?.abort();
    setPaused(true);
    setFinished(true);
  }

  function handleRetryFailed() {
    if (!form) return;
    for (const t of tasksRef.current) {
      if (t.status === "failed") {
        t.status = "pending";
        t.attempts = 0;
        t.error = undefined;
      }
    }
    void runUpload(tasksRef.current, form, sessionIdRef.current, false);
  }

  function handleNewUpload() {
    forgetSession();
    setResumable(null);
    setForm(null);
    setStats(EMPTY_STATS);
    setFailed([]);
    setFinished(false);
    setStep("form");
  }

  if (configError) {
    return (
      <Shell>
        <div className="card">
          <h2>Yapılandırma hatası</h2>
          <p className="warn">{configError}</p>
          <p className="muted">Lütfen sistem yöneticisi ile iletişime geçiniz.</p>
        </div>
      </Shell>
    );
  }

  if (!config) {
    return (
      <Shell>
        <div className="card">
          <p className="muted">Yükleniyor…</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {resumable && step === "form" && (
        <div className="card resume">
          <p>
            <strong>{resumable.form.provinceName}</strong> için tamamlanmamış bir yükleme bulundu.
            Aynı il ve aynı klasörü seçerek kaldığınız yerden devam edebilirsiniz — yüklenmiş
            görüntüler tekrar gönderilmez.
          </p>
          <div className="actions">
            <button
              type="button"
              className="primary"
              onClick={() => {
                setForm(resumable.form);
                setStep("pick");
              }}
            >
              Devam et
            </button>
            <button
              type="button"
              onClick={() => {
                forgetSession();
                setResumable(null);
              }}
            >
              Yoksay
            </button>
          </div>
        </div>
      )}

      {fatal && (
        <div className="card">
          <p className="warn">{fatal}</p>
        </div>
      )}

      {step === "form" && (
        <TeamForm
          onSubmit={(v) => {
            setForm(v);
            setStep("pick");
          }}
        />
      )}

      {step === "pick" && form && (
        <FolderPicker form={form} onBack={() => setStep("form")} onStart={handleStart} />
      )}

      {step === "upload" && form && (
        <UploadProgress
          form={form}
          stats={stats}
          failed={failed}
          finished={finished}
          paused={paused}
          onPause={handlePause}
          onRetryFailed={handleRetryFailed}
          onNewUpload={handleNewUpload}
        />
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="page">
      <header>
        <div className="brand">NOVANKYRA</div>
        <h1>AFAD Drone Ekipleri — Görüntü Veri Seti Yükleme</h1>
        <p className="muted">
          Yapay zekâ eğitim verisi toplama teşvik programı kapsamında RGB ve termal görüntü
          setlerinin teslim edildiği portaldır.
        </p>
      </header>
      <main>{children}</main>
      <footer className="muted small">
        NOVANKYRA Teknoloji A.Ş. · Sorun bildirimi için ekip sorumlunuz ile iletişime geçiniz.
      </footer>
    </div>
  );
}
